import { AlterErrorReportUpdateAction, IncorrectRecording } from '../../../ErrorReporting/types';
import {
  Type,
  spacer8,
  spacer24,
  TableCheckbox,
  spacer16,
  ButtonSecondary,
} from '@spotify-internal/encore-web';
import styled from 'styled-components';
import DataTable, { ColumnDefinition } from '../../../../components/DataTable';
import gTagManager from '../../../../libs/services/logging';
import { Link } from 'react-router-dom';
import { TitleLink } from '../../../../components/RecordingDetailsCell';
import recordingReducer, {
  getRecordingReducerState,
  TOGGLE_ONE,
  TOGGLE_ALL,
  UNSELECT_ALL,
  RecordingsState,
} from '../recordingReducer';
import { useReducer } from 'react';
import { isResolveButtonEnabled, isUnresolveButtonEnabled } from '../recordingTableActionsHelper';
import { useT } from '@spotify-internal/i18n-core';

type IncorrectRecordingsProps = {
  incorrectRecordings: IncorrectRecording[] | undefined;
  onUpdateIncorrectRecordings: (
    operation: AlterErrorReportUpdateAction,
    recordingGids: RecordingsState,
  ) => Promise<void>;
  songwriterOrgUri: string;
  songwriterDisplayName: string;
};

const Image = styled.img`
  height: 36px;
  margin-top: ${spacer8};
  margin-right: ${spacer8};
  margin-bottom: ${spacer8};
`;

const StyledCell = styled.div`
  font-weight: 'bold';
  font-size: 14;
  color: gray20;
  padding-left: 0;
`;

type IncorrectRecordingColumnKey =
  | 'select'
  | 'work'
  | 'matched_recording'
  | 'next_recording'
  | 'status';

const IncorrectRecordings = ({
  incorrectRecordings,
  onUpdateIncorrectRecordings,
  songwriterOrgUri,
  songwriterDisplayName,
}: IncorrectRecordingsProps) => {
  const t = useT();
  const [state, dispatch] = useReducer(
    recordingReducer,
    getRecordingReducerState(incorrectRecordings),
  );

  if (!incorrectRecordings || incorrectRecordings.length === 0) {
    return null;
  }

  const toggleAllRecordings = () => {
    if (state.allRecordingsChecked) {
      incorrectRecordings.forEach(fireUnselectRecordingEvent);
    } else {
      incorrectRecordings.filter(isRecordingUnselected).forEach(fireSelectRecordingEvent);
    }
    dispatch({
      type: TOGGLE_ALL,
    });
  };

  const unselectAllRecordings = () => {
    dispatch({
      type: UNSELECT_ALL,
    });
  };

  const toggleRecording = (incorrectRecording: IncorrectRecording) => {
    const recordingGid = incorrectRecording.recordingGid;

    if (isRecordingUnselected(incorrectRecording)) {
      fireSelectRecordingEvent(incorrectRecording);
    } else {
      fireUnselectRecordingEvent(incorrectRecording);
    }

    dispatch({
      type: TOGGLE_ONE,
      recordingGid,
    });
  };

  const isRecordingUnselected = (incorrectRecording: IncorrectRecording) => {
    return !state.recordings[incorrectRecording.recordingGid];
  };

  const isRecordingSelected = (incorrectRecording: IncorrectRecording) => {
    return state.recordings[incorrectRecording.recordingGid];
  };

  function fireSelectRecordingEvent(incorrectRecording: IncorrectRecording) {
    gTagManager({
      event: 'selectIncorrectRecording',
      incorrectRecording: `${incorrectRecording.recordingName} by ${incorrectRecording.recordingArtistName}`,
      creatorEntityUri: songwriterOrgUri,
      songwriterDisplayName: songwriterDisplayName,
      workGid: incorrectRecording.workGid,
      workTitle: incorrectRecording.workName,
      recordingTitle: incorrectRecording.recordingName,
    });
  }

  function fireUnselectRecordingEvent(incorrectRecording: IncorrectRecording) {
    gTagManager({
      event: 'unselectIncorrectRecording',
      incorrectRecording: `${incorrectRecording.recordingName} by ${incorrectRecording.recordingArtistName}`,
      creatorEntityUri: songwriterOrgUri,
      songwriterDisplayName: songwriterDisplayName,
      workGid: incorrectRecording.workGid,
      workTitle: incorrectRecording.workName,
      recordingTitle: incorrectRecording.recordingName,
    });
  }

  function fireResolveRecordingEvent(incorrectRecording: IncorrectRecording) {
    gTagManager({
      event: 'resolveIncorrectRecording',
      incorrectRecording: `${incorrectRecording.recordingName} by ${incorrectRecording.recordingArtistName}`,
      creatorEntityUri: songwriterOrgUri,
      songwriterDisplayName: songwriterDisplayName,
      workGid: incorrectRecording.workGid,
      workTitle: incorrectRecording.workName,
      recordingTitle: incorrectRecording.recordingName,
    });
  }

  function fireUnresolveRecordingEvent(incorrectRecording: IncorrectRecording) {
    gTagManager({
      event: 'unresolveIncorrectRecording',
      incorrectRecording: `${incorrectRecording.recordingName} by ${incorrectRecording.recordingArtistName}`,
      creatorEntityUri: songwriterOrgUri,
      songwriterDisplayName: songwriterDisplayName,
      workGid: incorrectRecording.workGid,
      workTitle: incorrectRecording.workName,
      recordingTitle: incorrectRecording.recordingName,
    });
  }

  function fireDeleteRecordingEvent(incorrectRecording: IncorrectRecording) {
    gTagManager({
      event: 'deleteIncorrectRecording',
      incorrectRecording: `${incorrectRecording.recordingName} by ${incorrectRecording.recordingArtistName}`,
      creatorEntityUri: songwriterOrgUri,
      songwriterDisplayName: songwriterDisplayName,
      workGid: incorrectRecording.workGid,
      workTitle: incorrectRecording.workName,
      recordingTitle: incorrectRecording.recordingName,
    });
  }

  return (
    <div>
      <Type as="h3" variant="celloCanon">
        {t(
          'H_INCORRECTREC_0',
          'Remove incorrectly matched recordings',
          'Header for page to remove incorrectly matched recordings',
        )}
      </Type>
      <Type
        as="p"
        variant="mesto"
        semanticColor="textSubdued"
        style={{ marginBlockStart: spacer8, marginBlockEnd: spacer24 }}
      >
        {t(
          'P_INCORRECTREC_0',
          'The songwriter reported these recordings as incorrect. If you remove them from the work, they will no longer appear on the songwriter page and the next match will appear instead. If the next match is also incorrect, please re-upload your data.',
          'Description about incorrectly matched recordings',
        )}
      </Type>
      <DataTable
        items={incorrectRecordings}
        keyForItem={(incorrectRecording) => `${incorrectRecording.recordingGid}`}
        columns={
          [
            {
              key: 'select',
              title: (
                <TableCheckbox
                  id="incorrect-recordings-checkbox-select-all"
                  onChange={toggleAllRecordings}
                  checked={state.allRecordingsChecked}
                />
              ),
            },
            {
              key: 'work',
              title: (
                <Type as="label">
                  {t(
                    'L_INCORRECTREC_0',
                    'Work',
                    'Column header for table of incorrectly matched recordings',
                  )}
                </Type>
              ),
            },
            {
              key: 'matched_recording',
              title: (
                <Type as="label">
                  {t(
                    'L_INCORRECTREC_1',
                    'Matched recording',
                    'Column header for table of incorrectly matched recordings',
                  )}
                </Type>
              ),
              minViewport: 'SM',
            },
            {
              key: 'next_recording',
              title: (
                <Type as="label">
                  {t(
                    'L_INCORRECTREC_2',
                    'Next recording',
                    'Column header for table of incorrectly matched recordings',
                  )}
                </Type>
              ),
              minViewport: 'MD',
            },
            {
              key: 'status',
              title: (
                <Type as="label">
                  {t(
                    'L_INCORRECTREC_3',
                    'Status',
                    'Column header for table of incorrectly matched recordings',
                  )}
                </Type>
              ),
              width: '100px',
            },
          ] as ColumnDefinition<IncorrectRecording, IncorrectRecordingColumnKey>[]
        }
        renderCell={(incorrectRecording, _, column) => {
          const recordingInfo = `${incorrectRecording.recordingName} - ${incorrectRecording.recordingArtistName}`;
          const alText = t(
            'ALTTEXT_INCORRECTREC_0',
            'Cover art for {recordingName} by {artistName}',
            'Alt text for recording cover art',
            {
              recordingName: incorrectRecording.recordingName,
              artistName: incorrectRecording.recordingArtistName,
            },
          );
          const nextRecordingInfo = incorrectRecording.nextRecordingName
            ? `${incorrectRecording.nextRecordingName} - ${incorrectRecording.nextRecordingArtistName}`
            : ``;
          const nextAltText = incorrectRecording.nextRecordingName
            ? t(
                'ALTTEXT_INCORRECTREC_1',
                'Cover art for {recordingName} by {artistName}',
                'Alt text for recording cover art',
                {
                  recordingName: incorrectRecording.nextRecordingName,
                  artistName: incorrectRecording.nextRecordingArtistName,
                },
              )
            : ``;
          const recordingLink = `../../work/${incorrectRecording.workGid}/recording/${incorrectRecording.recordingGid}`;
          const nextRecordingLink = `../../work/${incorrectRecording.workGid}/recording/${incorrectRecording.nextRecordingGid}`;
          switch (column.key) {
            case 'select':
              return (
                <TableCheckbox
                  id={`incorrect-recordings-checkbox-select-${incorrectRecording.recordingGid}`}
                  onChange={() => toggleRecording(incorrectRecording)}
                  checked={state.recordings[incorrectRecording.recordingGid]}
                />
              );
            case 'work':
              return (
                <Link
                  data-test-id={`work-link-${incorrectRecording.workGid}`}
                  to={`/work/${incorrectRecording.workGid}`}
                  onClick={() => {
                    gTagManager({
                      event: 'selectWork',
                      workGid: incorrectRecording.workGid,
                      workTitle: incorrectRecording.workGid,
                    });
                  }}
                >
                  {incorrectRecording.workName}
                </Link>
              );
            case 'matched_recording':
              return (
                <StyledCell style={{ display: 'flex', alignItems: 'center', paddingLeft: 0 }}>
                  <Image src={incorrectRecording.coverArt300} alt={alText} />
                  <TitleLink
                    to={recordingLink}
                    onClick={() =>
                      gTagManager({
                        event: 'selectRecording',
                        recordingGid: incorrectRecording.recordingGid,
                        recordingTitle: incorrectRecording.recordingName,
                        recordingArtist: incorrectRecording.recordingArtistName,
                      })
                    }
                  >
                    {recordingInfo}
                  </TitleLink>
                </StyledCell>
              );
            case 'next_recording':
              return incorrectRecording.nextRecordingName ? (
                <StyledCell style={{ display: 'flex', alignItems: 'center' }}>
                  <Image src={incorrectRecording.nextRecordingCoverArt300} alt={nextAltText} />
                  <TitleLink
                    to={nextRecordingLink}
                    onClick={() =>
                      gTagManager({
                        event: 'selectRecording',
                        recordingGid: incorrectRecording.nextRecordingGid,
                        recordingTitle: incorrectRecording.nextRecordingName,
                        recordingArtist: incorrectRecording.nextRecordingArtistName,
                      })
                    }
                  >
                    {nextRecordingInfo}
                  </TitleLink>
                </StyledCell>
              ) : (
                <StyledCell />
              );
            case 'status':
              return (
                <Type semanticColor={incorrectRecording.resolved ? 'textPositive' : 'textSubdued'}>
                  {incorrectRecording.resolved
                    ? t(
                        'TEXT_INCORRECTREC_0',
                        'Resolved',
                        'Text that shows when an incorrect recording has a status of "Resolved"',
                      )
                    : t(
                        'TEXT_INCORRECTREC_1',
                        'Unresolved',
                        'Text that shows when an incorrect recording has a status of "Unresolved"',
                      )}
                </Type>
              );
          }
        }}
      />
      <div style={{ marginBlockStart: spacer16, display: 'flex', gap: spacer16 }}>
        <ButtonSecondary
          buttonSize="sm"
          onClick={async () => {
            await onUpdateIncorrectRecordings('DELETE', state.recordings);
            incorrectRecordings.filter(isRecordingSelected).forEach(fireDeleteRecordingEvent);
            unselectAllRecordings();
          }}
          disabled={!state.anyRecordingsChecked}
          style={{ display: 'none' }}
        >
          {t(
            'BUTTON_INCORRECTREC_0',
            'Delete',
            'Button to remove an incorrect recording from the table',
          )}
        </ButtonSecondary>
        <ButtonSecondary
          buttonSize="sm"
          onClick={async () => {
            await onUpdateIncorrectRecordings('RESOLVE', state.recordings);
            incorrectRecordings.filter(isRecordingSelected).forEach(fireResolveRecordingEvent);
            unselectAllRecordings();
          }}
          disabled={!isResolveButtonEnabled(state, incorrectRecordings)}
        >
          {t(
            'BUTTON_INCORRECTREC_1',
            'Mark as resolved',
            'Button to mark an incorrect recording as resolved',
          )}
        </ButtonSecondary>
        <ButtonSecondary
          buttonSize="sm"
          onClick={async () => {
            await onUpdateIncorrectRecordings('UNRESOLVE', state.recordings);
            incorrectRecordings.filter(isRecordingSelected).forEach(fireUnresolveRecordingEvent);
            unselectAllRecordings();
          }}
          disabled={!isUnresolveButtonEnabled(state, incorrectRecordings)}
        >
          {t(
            'BUTTON_INCORRECTREC_2',
            'Mark as unresolved',
            'Button to mark an incorrect recording as unresolved',
          )}
        </ButtonSecondary>
      </div>
    </div>
  );
};

export default IncorrectRecordings;
